import {
  prepareProposalPdfImages,
  type PdfImageReadOptions,
} from '@/lib/sales/prepare-pdf-line-images';
import type { ProposalPdfCompany } from '@/components/documents/proposals/ProposalPDF';
import type { ProposalItemizedPdfSource } from '@/components/documents/proposals/ProposalItemizedPdfDocument';
import { isSingleScopeLayout } from '@/lib/quote/singleScopeTheme';

/**
 * What the signed proposal document renders from. The signature arrives as the
 * PNG data URL the pad produced: the stored copy is a presigned private-bucket
 * URL that react-pdf cannot fetch cross-origin, so the browser that took the
 * signature is the only place this document can be made.
 */
export interface SignedProposalPdfContent {
  proposal: ProposalItemizedPdfSource;
  company: ProposalPdfCompany;
}

/** Render the signed proposal document in the browser. A single-scope design
 *  prints the single-scope proposal PDF so the signed copy matches the surface
 *  the customer accepted on; itemized documents reuse their resolved design. */
export async function renderSignedProposalPdf(
  content: SignedProposalPdfContent,
  imageRead: PdfImageReadOptions = {},
): Promise<Blob> {
  const proposal = await prepareProposalPdfImages(content.proposal, imageRead);
  const { pdf } = await import('@react-pdf/renderer');
  if (isSingleScopeLayout(content.proposal.resolvedDocLayout)) {
    const { ProposalSingleScopePdfDocument } =
      await import('@/components/documents/proposals/ProposalSingleScopePdfDocument');
    return pdf(
      <ProposalSingleScopePdfDocument proposal={proposal} company={content.company} />,
    ).toBlob();
  }
  const { ProposalItemizedPdfDocument } =
    await import('@/components/documents/proposals/ProposalItemizedPdfDocument');
  return pdf(
    <ProposalItemizedPdfDocument proposal={proposal} company={content.company} />,
  ).toBlob();
}

/**
 * Upload a rendered signed proposal PDF and register it as that proposal's
 * document of record. The endpoints are the `@Public()`-by-id pair the customer
 * page and the portal both reach; the server pins the registered URL to the
 * proposal's own S3 prefix and only accepts one while the acceptance is fresh.
 */
export async function persistSignedProposalPdf(params: {
  opsBaseUrl: string;
  id: string;
  reason: string;
  blob: Blob;
}): Promise<void> {
  const { opsBaseUrl, id, reason, blob } = params;

  const uploadRes = await fetch(`${opsBaseUrl}/proposals/public/${id}/pdf-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!uploadRes.ok) {
    throw new Error(`Could not get upload URL (${uploadRes.status})`);
  }
  const { uploadUrl, fileUrl } = await uploadRes.json();

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'application/pdf' },
  });
  if (!putRes.ok) {
    throw new Error(`Signed PDF upload failed (${putRes.status})`);
  }

  const registerRes = await fetch(`${opsBaseUrl}/proposals/public/${id}/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: fileUrl, reason }),
  });
  if (!registerRes.ok) {
    throw new Error(`Could not save signed PDF (${registerRes.status})`);
  }
}

/**
 * How long the customer's page will wait for the signed document before moving
 * on. Long enough for a render and upload on a phone, short enough that a
 * hung network never traps someone who has already accepted.
 */
export const SIGNED_RECORD_DEADLINE_MS = 8000;

/**
 * Wait for the signed document to be written, but never longer than
 * `deadlineMs`, and never fail.
 *
 * A page that navigates away cannot fire-and-forget this work: the render and
 * the two uploads die with the document, and the record is simply never
 * written. Nor can it wait indefinitely — the acceptance has already
 * succeeded, so a slow upload must not strand the customer. Bounded waiting is
 * the only shape that produces the record in the normal case AND always lets
 * them through.
 */
export async function withSignedRecordDeadline(
  work: Promise<unknown>,
  deadlineMs: number = SIGNED_RECORD_DEADLINE_MS,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      work.catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, deadlineMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Render, upload and register in one call.
 *
 * Deliberately best-effort at every call site: unlike the estimate flow, where
 * the PDF is registered BEFORE the approval and so can gate it, a proposal is
 * already accepted by the time this document can exist. Failing the customer's
 * accept because a convenience artifact did not upload would be the wrong
 * trade — the durable record is the acceptance row and the stored signature.
 */
export async function registerSignedProposalPdf(params: {
  opsBaseUrl: string;
  id: string;
  reason: string;
  content: SignedProposalPdfContent;
}): Promise<void> {
  const { content, ...persist } = params;
  await persistSignedProposalPdf({
    ...persist,
    blob: await renderSignedProposalPdf(content, { opsBaseUrl: params.opsBaseUrl, id: params.id }),
  });
}
